/**
 * Bundle Offers Component
 * Manages bundle/multi-piece offers with variant selection for additional pieces.
 * The first piece is always the variant selected in the main product variant picker.
 */

class BundleOffersComponent extends HTMLElement {
  constructor() {
    super();

    this.productId = this.dataset.productId;
    this.sectionId = this.dataset.sectionId;
    this.blockId = this.dataset.blockId;
    this.productUrl = this.dataset.productUrl;

    // State
    this.selectedOffer = null;
    // Per-offer selections to avoid cross-offer contamination:
    // { [offerIndex]: { [pieceIndex]: { [optionHandle]: value, variantId } } }
    this.bundleSelectionsByOffer = {};
    this.activeOfferIndex = null;
    this.variants = [];
    this.productOptions = [];
    this.hasOptions = false;
    this.defaultVariantId = null;
    this.mainVariantId = null;

    // Current modal context
    this.currentModalContext = null; // { offerIndex, pieceIndex, optionName }

    // DOM refs (populated in initRefs)
    this.offersList = null;
    this.optionModal = null;
    this.modalTitle = null;
    this.modalOptions = null;
    this.validationMessage = null;
    this.validationText = null;

    // Lifecycle handles — created fresh on each connectedCallback, torn down on disconnect.
    // Using AbortController lets us remove all event listeners (including DOM-element ones)
    // with a single abort() call, matching the pattern used by product-form-component.
    this._abortController = null;
    this._validationTimer = null; // auto-hide timer for the validation message
    this._modalCloseTimer = null; // deferred close timer after option selection
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this.initRefs();
    this.loadProductData();
    this.initializePerkPanels();
    this.bindEvents();
    this.initializeFromMainPicker();
    this.autoSelectFirstOffer();
    // Defer the initial state publish via microtask so that product-form-component
    // has a chance to register its bundle-offers:state listener, even when this
    // element is upgraded before the form in the DOM.
    queueMicrotask(() => this.publishBundleState());
  }

  disconnectedCallback() {
    // Restore body scroll if the option modal was open when the component was removed.
    if (this.optionModal?.getAttribute('aria-hidden') === 'false') {
      this.closeModal();
    }

    // Cancel any in-flight timers before nulling the controller.
    if (this._validationTimer !== null) {
      clearTimeout(this._validationTimer);
      this._validationTimer = null;
    }
    if (this._modalCloseTimer !== null) {
      clearTimeout(this._modalCloseTimer);
      this._modalCloseTimer = null;
    }

    // Remove every event listener registered with this controller in one call.
    this._abortController?.abort();
    this._abortController = null;

    // Notify product-form that the bundle is no longer active.
    this.selectedOffer = null;
    this.activeOfferIndex = null;
    this.publishBundleState();
  }

  initRefs() {
    this.offersList = this.querySelector('[ref="offersList"]');
    this.optionModal = this.querySelector('[ref="optionModal"]');
    this.modalTitle = this.querySelector('[ref="modalTitle"]');
    this.modalOptions = this.querySelector('[ref="modalOptions"]');
    this.validationMessage = this.querySelector('[ref="validationMessage"]');
    this.validationText = this.querySelector('[ref="validationText"]');
  }

  loadProductData() {
    try {
      const variantsScript = this.querySelector('[data-product-variants]');
      const optionsScript = this.querySelector('[data-product-options]');

      if (variantsScript) {
        this.variants = JSON.parse(variantsScript.textContent);
      }

      if (optionsScript) {
        this.productOptions = JSON.parse(optionsScript.textContent);
      }
    } catch (e) {
      console.error('BundleOffersComponent: error loading product data:', e);
    }

    this.hasOptions = this.productOptions.length > 0;
    if (!this.hasOptions && this.variants.length > 0) {
      this.defaultVariantId = this.variants[0].id;
    }
  }

  initializePerkPanels() {
    const panels = this.querySelectorAll('[data-perk-panel]');
    if (!panels.length) return;

    [
      {
        panelId: 'perk1',
        sourceKeywords: [
          'backup nasal adapter',
          'backup nasal adapters',
          'replacement spare part',
          'replacement spare parts',
        ],
      },
      {
        panelId: 'perk2',
        sourceKeywords: ['airin e book', 'airin ebook', 'complimentary e book', 'complimentary ebook'],
      },
    ].forEach(config => this.populatePerkPanel(config));
  }

  populatePerkPanel({ panelId, sourceKeywords }) {
    const panelRoot = this.querySelector(`[data-perk-panel="${panelId}"]`);
    if (!panelRoot) return;

    const mediaTarget = panelRoot.querySelector(`[data-perk-panel-media="${panelId}"]`);
    const summaryTarget = panelRoot.querySelector(`[data-perk-panel-summary="${panelId}"]`);
    const highlightsTarget = panelRoot.querySelector(`[data-perk-panel-highlights="${panelId}"]`);
    const eyebrowTarget = panelRoot.querySelector(`[data-perk-panel-eyebrow="${panelId}"]`);

    const sourceItem = this.findPerkPanelSource(sourceKeywords);
    const hasConfiguredContent = this.hasPerkPanelContent({
      mediaTarget,
      summaryTarget,
      highlightsTarget,
      eyebrowTarget,
    });

    if (!sourceItem && !hasConfiguredContent) {
      this.disablePerkPanelTriggers(panelId);
      return;
    }

    if (sourceItem) {
      this.populatePerkPanelMedia(mediaTarget, sourceItem);
      this.populatePerkPanelEyebrow(eyebrowTarget, sourceItem);
      this.populatePerkPanelSummary(summaryTarget, sourceItem);
    }

    this.finalizePerkPanel(panelRoot, {
      mediaTarget,
      summaryTarget,
      highlightsTarget,
      eyebrowTarget,
    });
  }

  hasPerkPanelContent({ mediaTarget, summaryTarget, highlightsTarget, eyebrowTarget }) {
    return (
      this.hasMeaningfulContent(mediaTarget) ||
      this.hasMeaningfulContent(summaryTarget) ||
      Boolean(highlightsTarget?.querySelector('li')) ||
      Boolean(eyebrowTarget?.textContent?.trim())
    );
  }

  hasMeaningfulContent(node) {
    if (!node) return false;

    return Boolean(node.textContent.trim()) || Boolean(node.querySelector('img, svg, picture, video'));
  }

  disablePerkPanelTriggers(panelId) {
    this.querySelectorAll(`[data-perk-trigger-icon="${panelId}"]`).forEach(trigger => {
      trigger.hidden = true;
      trigger.setAttribute('aria-hidden', 'true');
      trigger.tabIndex = -1;
    });

    this.querySelectorAll(`[data-perk-trigger-label="${panelId}"]`).forEach(trigger => {
      trigger.disabled = true;
      trigger.setAttribute('aria-disabled', 'true');
    });

    this.querySelectorAll(`[data-perk-label="${panelId}"]`).forEach(label => {
      label.classList.remove('bundle-offers__perk-text--interactive');
    });
  }

  populatePerkPanelMedia(mediaTarget, sourceItem) {
    if (!mediaTarget || this.hasMeaningfulContent(mediaTarget)) return;

    const media = sourceItem.querySelector('.what-included-box__media')?.cloneNode(true);
    if (!media) return;

    mediaTarget.replaceChildren(media);
  }

  populatePerkPanelEyebrow(eyebrowTarget, sourceItem) {
    if (!eyebrowTarget || eyebrowTarget.textContent.trim()) return;

    const sourceCaption = sourceItem.querySelector('.what-included-box__caption');
    if (!sourceCaption?.textContent?.trim()) return;

    eyebrowTarget.textContent = sourceCaption.textContent.trim();
    eyebrowTarget.hidden = false;
  }

  populatePerkPanelSummary(summaryTarget, sourceItem) {
    if (!summaryTarget || this.hasMeaningfulContent(summaryTarget)) return;

    const fragment = document.createDocumentFragment();
    const sourceLabel = sourceItem.querySelector('.what-included-box__label');
    const sourceDescription = sourceItem.querySelector('.what-included-box__description');
    const panelTitle = summaryTarget
      .closest('[data-perk-panel]')
      ?.querySelector('.bundle-offers__perk-panel-title')
      ?.textContent?.trim();

    if (
      sourceLabel?.textContent?.trim() &&
      this.normalizeText(sourceLabel.textContent) !== this.normalizeText(panelTitle || '')
    ) {
      const clonedLabel = sourceLabel.cloneNode(true);
      clonedLabel.classList.add('bundle-offers__perk-panel-summary-label');
      fragment.append(clonedLabel);
    }

    if (sourceDescription) {
      const clonedDescription = sourceDescription.cloneNode(true);
      clonedDescription.classList.add('bundle-offers__perk-panel-summary-description');
      fragment.append(clonedDescription);
    }

    if (fragment.childNodes.length > 0) {
      summaryTarget.replaceChildren(fragment);
    }
  }

  finalizePerkPanel(panelRoot, { mediaTarget, summaryTarget, highlightsTarget, eyebrowTarget }) {
    const hasMedia = this.hasMeaningfulContent(mediaTarget);
    const hasSummary = this.hasMeaningfulContent(summaryTarget);
    const hasHighlights = Boolean(highlightsTarget?.querySelector('li'));
    const hasEyebrow = Boolean(eyebrowTarget?.textContent?.trim());

    if (eyebrowTarget) {
      eyebrowTarget.hidden = !hasEyebrow;
    }

    if (summaryTarget) {
      summaryTarget.hidden = !hasSummary;
      summaryTarget.classList.toggle('bundle-offers__perk-panel-summary--empty', !hasSummary);
    }

    if (highlightsTarget) {
      highlightsTarget.hidden = !hasHighlights;
      highlightsTarget.classList.toggle('bundle-offers__perk-panel-highlights--empty', !hasHighlights);
    }

    const visual =
      panelRoot.querySelector('[data-perk-panel-visual]') ||
      panelRoot.querySelector('.bundle-offers__perk-panel-visual');
    if (visual) {
      visual.hidden = !hasMedia;
    }

    panelRoot.classList.toggle('bundle-offers__perk-panel--text-only', !hasMedia);
    panelRoot.classList.toggle('perk-side-panel--text-only', !hasMedia);
  }

  findPerkPanelSource(sourceKeywords) {
    const includedItems = Array.from(document.querySelectorAll('.what-included-box__item'));
    if (!includedItems.length) return null;

    return (
      includedItems.find(item => {
        const itemText = this.normalizeText(item.textContent || '');
        return sourceKeywords.some(keyword => itemText.includes(this.normalizeText(keyword)));
      }) || null
    );
  }

  normalizeText(value) {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  bindEvents() {
    const { signal } = this._abortController;

    // Offer selection — click on row or card for toggle behaviour.
    this.querySelectorAll('.bundle-offers__offer-row, .bundle-offers__card-inner').forEach(row => {
      row.addEventListener('click', (e) => this.handleOfferClick(e), { signal });
    });

    // Offer close buttons.
    this.querySelectorAll('.bundle-offers__offer-close').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleOfferClose(e), { signal });
    });

    // Option triggers (open the variant-selection modal).
    this.querySelectorAll('.bundle-offers__option-trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => this.handleOptionTriggerClick(e), { signal });
    });

    // Modal close buttons (overlay + header ×).
    this.optionModal?.querySelectorAll('[data-close-modal]').forEach(el => {
      el.addEventListener('click', () => this.closeModal(), { signal });
    });

    // Modal option selection.
    this.optionModal?.querySelectorAll('input[type="radio"]').forEach(input => {
      input.addEventListener('change', (e) => this.handleModalOptionSelect(e), { signal });
    });

    // Escape key closes the modal.
    document.addEventListener('keydown', (e) => this.handleKeyDown(e), { signal });

    // Listen for variant updates from the main picker.
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener('variant:update', (e) => this.handleMainVariantUpdate(e), { signal });

    // Also listen for the theme-level variant change event.
    document.addEventListener('theme:variant:change', (e) => this.handleMainVariantUpdate(e), { signal });

    // Receive validation messages dispatched by product-form-component.
    document.addEventListener('bundle-offers:validation', (e) => this.handleBundleValidation(e), { signal });
  }

  initializeFromMainPicker() {
    // Get current variant from the main picker.
    const variantPicker = document.querySelector(`variant-picker[data-section-id="${this.sectionId}"]`);
    if (variantPicker) {
      const variantJson = variantPicker.querySelector('script[type="application/json"]');
      if (variantJson) {
        try {
          const currentVariant = JSON.parse(variantJson.textContent);
          this.mainVariantId = currentVariant?.id;
        } catch (e) {
          console.error('BundleOffersComponent: error parsing main variant:', e);
        }
      }
    }

    // Fallback: read from the hidden variant input on the product form.
    if (!this.mainVariantId) {
      const variantInput = document.querySelector(
        `product-form-component[data-section-id="${this.sectionId}"] input[name="id"]`
      );
      if (variantInput) {
        this.mainVariantId = variantInput.value;
      }
    }
  }

  autoSelectFirstOffer() {
    const firstRadio = this.querySelector('.bundle-offers__radio');
    if (!firstRadio) return;
    const offerIndex = firstRadio.value;
    const pieces = parseInt(firstRadio.dataset.pieces, 10);
    const discount = parseInt(firstRadio.dataset.discount, 10) || 0;
    this.selectOffer(offerIndex, pieces, discount);
  }

  handleOfferClick(event) {
    // Prevent native label behaviour from interfering with our custom radio handling.
    // Without this the browser re-checks the radio after we programmatically uncheck it.
    event.preventDefault();

    // Don't toggle when clicking option triggers inside the selector panel.
    if (event.target.closest('.bundle-offers__option-trigger')) return;

    const row = event.currentTarget;
    const radio = row.querySelector('.bundle-offers__radio');
    if (!radio) return;

    const offerIndex = radio.value;
    const pieces = parseInt(radio.dataset.pieces, 10);

    // Toggle: clicking the active offer deselects it.
    if (this.activeOfferIndex === offerIndex) {
      this.deselectOffer();
      return;
    }

    this.selectOffer(offerIndex, pieces, parseInt(radio.dataset.discount, 10));
  }

  selectOffer(offerIndex, pieces, discount) {
    // Uncheck all radios first.
    this.querySelectorAll('.bundle-offers__radio').forEach(r => (r.checked = false));

    // Check the selected radio.
    const radio = this.querySelector(`.bundle-offers__radio[value="${offerIndex}"]`);
    if (radio) radio.checked = true;

    this.selectedOffer = { index: offerIndex, pieces, discount };
    this.activeOfferIndex = offerIndex;

    // Update offer row active states.
    this.querySelectorAll('.bundle-offers__offer').forEach(offer => {
      offer.classList.toggle('bundle-offers__offer--active', offer.dataset.offerIndex === offerIndex);
    });

    // Close all other panels first.
    this.querySelectorAll('.bundle-offers__selectors-wrapper').forEach(wrapper => {
      if (wrapper.dataset.selectorsFor !== offerIndex) {
        wrapper.classList.remove('bundle-offers__selectors-wrapper--open');
      }
    });

    // Open the selected panel.
    const selectorsWrapper = this.querySelector(`[data-selectors-for="${offerIndex}"]`);
    if (selectorsWrapper) {
      requestAnimationFrame(() => {
        selectorsWrapper.classList.add('bundle-offers__selectors-wrapper--open');

        const selectorsInner = selectorsWrapper.querySelector('.bundle-offers__selectors-inner');
        if (!selectorsInner) {
          // Wrapper exists but inner container is missing: auto-init pieces with first variant.
          if (!this.bundleSelectionsByOffer[offerIndex]) this.bundleSelectionsByOffer[offerIndex] = {};
          const fallbackId = this.defaultVariantId || this.variants[0]?.id || null;
          for (let i = 2; i <= pieces; i++) {
            if (!this.bundleSelectionsByOffer[offerIndex][i]) {
              this.bundleSelectionsByOffer[offerIndex][i] = { variantId: fallbackId };
            }
          }
          this.publishBundleState();
          return;
        }

        for (let i = 2; i <= pieces; i++) {
          if (!this.bundleSelectionsByOffer[offerIndex]) this.bundleSelectionsByOffer[offerIndex] = {};
          if (!this.bundleSelectionsByOffer[offerIndex][i]) {
            this.initializePieceSelections(offerIndex, i, selectorsInner);
          } else {
            this.applyPieceSelectionsToUI(offerIndex, i, selectorsInner);
          }
        }
        // Re-publish after piece selections are initialised so product-form receives complete variant IDs.
        this.publishBundleState();
      });
    } else {
      // No selectors panel rendered: covers has_only_default_variant products (where
      // options_with_values still returns JSON options but the Liquid skips the selector UI)
      // and single-piece offers. Auto-initialise extra pieces with the first available variant.
      if (!this.bundleSelectionsByOffer[offerIndex]) this.bundleSelectionsByOffer[offerIndex] = {};
      const autoVariantId = this.defaultVariantId || this.variants[0]?.id || null;
      for (let i = 2; i <= pieces; i++) {
        this.bundleSelectionsByOffer[offerIndex][i] = { variantId: autoVariantId };
      }
    }

    this.hideValidationMessage();
    this.updatePerksDisplay(offerIndex);
    this.publishBundleState();
  }

  deselectOffer() {
    // Uncheck all radios.
    this.querySelectorAll('.bundle-offers__radio').forEach(r => (r.checked = false));

    // Remove active states.
    this.querySelectorAll('.bundle-offers__offer').forEach(offer => {
      offer.classList.remove('bundle-offers__offer--active');
    });

    // Close all panels.
    this.querySelectorAll('.bundle-offers__selectors-wrapper').forEach(wrapper => {
      wrapper.classList.remove('bundle-offers__selectors-wrapper--open');
    });

    this.selectedOffer = null;
    this.activeOfferIndex = null;

    this.hideValidationMessage();
    this.updatePerksDisplay(null);
    this.publishBundleState();
  }

  updatePerksDisplay(offerIndex) {
    this.querySelectorAll('[data-perks-for]').forEach(el => {
      el.hidden = el.dataset.perksFor !== String(offerIndex);
    });
  }

  handleOfferClose(event) {
    event.preventDefault();
    event.stopPropagation();
    this.deselectOffer();
  }

  initializePieceSelections(offerIndex, pieceIndex, selectorsPanel) {
    if (!selectorsPanel) return;
    const pieceEl = selectorsPanel.querySelector(`[data-piece-index="${pieceIndex}"]`);
    if (!pieceEl) return;

    const selections = {};

    // Initialise with the first available value for each option.
    this.productOptions.forEach(option => {
      const optionHandle = this.handleize(option.name);
      const firstAvailableValue = option.values.find(v => v.available !== false);
      selections[optionHandle] = firstAvailableValue?.name || option.values[0]?.name || option.values[0];

      // Update the display trigger.
      const trigger = pieceEl.querySelector(`[data-option="${optionHandle}"]`);
      if (trigger) {
        const valueEl = trigger.querySelector('[data-selected-value]');
        if (valueEl) valueEl.textContent = selections[optionHandle];
      }
    });

    // Find the matching variant.
    const variantId = this.findVariantId(selections);

    if (!this.bundleSelectionsByOffer[offerIndex]) this.bundleSelectionsByOffer[offerIndex] = {};
    this.bundleSelectionsByOffer[offerIndex][pieceIndex] = { ...selections, variantId };
  }

  handleOptionTriggerClick(event) {
    const trigger = event.currentTarget;
    const pieceIndex = parseInt(trigger.dataset.piece, 10);
    const optionName = trigger.dataset.option;
    const offerEl = trigger.closest('.bundle-offers__offer');
    const offerIndex = offerEl?.dataset?.offerIndex || this.activeOfferIndex;

    this.currentModalContext = { offerIndex, pieceIndex, optionName };
    this.openModal(optionName);
  }

  openModal(optionName) {
    if (!this.optionModal) return;

    // Find the option display name.
    const optionData = this.productOptions.find(o => this.handleize(o.name) === optionName);
    const displayName = optionData?.name || optionName;

    if (this.modalTitle) this.modalTitle.textContent = displayName;

    // Show the correct option group.
    this.modalOptions?.querySelectorAll('.bundle-offers__modal-option-group').forEach(group => {
      group.hidden = group.dataset.optionName !== optionName;
    });

    // Pre-select the currently saved value.
    if (this.currentModalContext) {
      const { offerIndex, pieceIndex, optionName: opt } = this.currentModalContext;
      const currentValue = this.bundleSelectionsByOffer?.[offerIndex]?.[pieceIndex]?.[opt];
      if (currentValue) {
        const radio = this.optionModal.querySelector(
          `.bundle-offers__modal-option-group[data-option-name="${opt}"] input[value="${currentValue}"]`
        );
        if (radio) radio.checked = true;
      }
    }

    this.optionModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.optionModal) return;
    this.optionModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.currentModalContext = null;
  }

  handleModalOptionSelect(event) {
    const input = event.target;
    const selectedValue = input.value;

    if (!this.currentModalContext) return;

    const { offerIndex, pieceIndex, optionName } = this.currentModalContext;

    // Update the selection store.
    if (!this.bundleSelectionsByOffer[offerIndex]) this.bundleSelectionsByOffer[offerIndex] = {};
    if (!this.bundleSelectionsByOffer[offerIndex][pieceIndex]) {
      this.bundleSelectionsByOffer[offerIndex][pieceIndex] = {};
    }
    this.bundleSelectionsByOffer[offerIndex][pieceIndex][optionName] = selectedValue;

    // Resolve variant ID from updated selections.
    const variantId = this.findVariantId(this.bundleSelectionsByOffer[offerIndex][pieceIndex]);
    this.bundleSelectionsByOffer[offerIndex][pieceIndex].variantId = variantId;

    // Update the trigger display text.
    const selectorsPanel = this.querySelector(`[data-selectors-for="${offerIndex}"]`);
    const trigger = selectorsPanel?.querySelector(
      `.bundle-offers__piece[data-piece-index="${pieceIndex}"] [data-option="${optionName}"]`
    );
    if (trigger) {
      const valueEl = trigger.querySelector('[data-selected-value]');
      if (valueEl) valueEl.textContent = selectedValue;
    }

    // Defer modal close to give the user visual feedback of their selection.
    // Store the timer ID so disconnectedCallback can cancel it if needed.
    if (this._modalCloseTimer !== null) clearTimeout(this._modalCloseTimer);
    this._modalCloseTimer = setTimeout(() => {
      this._modalCloseTimer = null;
      this.closeModal();
    }, 150);

    this.hideValidationMessage();
    this.publishBundleState();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && this.optionModal?.getAttribute('aria-hidden') === 'false') {
      this.closeModal();
    }
  }

  handleMainVariantUpdate(event) {
    const newVariantId = event.detail?.resource?.id || event.detail?.variant?.id;
    if (newVariantId) this.mainVariantId = newVariantId;
  }

  isMultiPieceBundleSelected() {
    return Boolean(this.selectedOffer && Number(this.selectedOffer.pieces) > 1);
  }

  publishBundleState() {
    const state = this.selectedOffer
      ? {
          active: this.isMultiPieceBundleSelected(),
          offerIndex: this.selectedOffer.index,
          pieces: this.selectedOffer.pieces,
          discount: this.selectedOffer.discount,
          selections: this.bundleSelectionsByOffer?.[this.selectedOffer.index] || {},
        }
      : { active: false };

    document.dispatchEvent(
      new CustomEvent('bundle-offers:state', {
        detail: { sectionId: this.sectionId, productId: this.productId, state },
      })
    );
  }

  handleBundleValidation(event) {
    if (event?.detail?.sectionId && event.detail.sectionId !== this.sectionId) return;
    if (event?.detail?.message) this.showValidationMessage(event.detail.message);
  }

  applyPieceSelectionsToUI(offerIndex, pieceIndex, selectorsPanel) {
    const pieceEl = selectorsPanel.querySelector(`[data-piece-index="${pieceIndex}"]`);
    if (!pieceEl) return;

    const saved = this.bundleSelectionsByOffer?.[offerIndex]?.[pieceIndex];
    if (!saved) return;

    this.productOptions.forEach(option => {
      const optionHandle = this.handleize(option.name);
      const value = saved[optionHandle];
      if (!value) return;

      const trigger = pieceEl.querySelector(`[data-option="${optionHandle}"]`);
      const valueEl = trigger?.querySelector('[data-selected-value]');
      if (valueEl) valueEl.textContent = value;
    });
  }

  findVariantId(selections) {
    // Exclude the stored variantId key from option comparisons.
    const optionSelections = { ...selections };
    delete optionSelections.variantId;

    const matchingVariant = this.variants.find(variant =>
      this.productOptions.every((option, index) => {
        const optionHandle = this.handleize(option.name);
        return optionSelections[optionHandle] === variant[`option${index + 1}`];
      })
    );

    return matchingVariant?.id || null;
  }

  handleize(str) {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  showValidationMessage(message) {
    if (!this.validationMessage || !this.validationText) return;

    // Clear any existing auto-hide timer before starting a new one.
    if (this._validationTimer !== null) {
      clearTimeout(this._validationTimer);
      this._validationTimer = null;
    }

    this.validationText.textContent = message;
    this.validationMessage.hidden = false;

    this._validationTimer = setTimeout(() => {
      this._validationTimer = null;
      this.hideValidationMessage();
    }, 5000);
  }

  hideValidationMessage() {
    if (this.validationMessage) this.validationMessage.hidden = true;
  }
}

if (!customElements.get('bundle-offers-component')) {
  customElements.define('bundle-offers-component', BundleOffersComponent);
}
